import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  MapPin,
  Clock,
  Play,
  Square,
  CheckCircle,
  AlertCircle,
  User,
  Calendar,
  Timer,
  Navigation,
  Fingerprint,
  FileText,
  Download,
  Filter,
  RefreshCw,
  BarChart3,
  TrendingUp,
  Users,
  Award,
  Flag,
  AlertTriangle,
  Bell,
  Eye,
  XCircle,
  Printer,
  Zap,
  LogIn,
  LogOut,
  Upload,
  FileJson,
  CheckCircle2,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { SandataExportTab } from "@/components/evv";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EVVPage() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [activeVisit, setActiveVisit] = useState(null);
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Permission checks
  const canClock = hasPermission("evv", "clock");
  const canVerify = hasPermission("evv", "verify");
  const canFlag = hasPermission("evv", "flag");
  const canViewReports = hasPermission("evv", "reports");
  
  // Clock In Dialog
  const [clockInDialog, setClockInDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState("");
  const [clockInNotes, setClockInNotes] = useState("");
  
  // Clock Out Dialog
  const [clockOutDialog, setClockOutDialog] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState("");
  const [servicesProvided, setServicesProvided] = useState("");
  
  // Admin Filter
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Verify Visit Dialog (with documentation-incentive checkbox)
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyingVisit, setVerifyingVisit] = useState(null);
  const [verifyAwardIncentive, setVerifyAwardIncentive] = useState(false);
  const [verifyIncentiveRate, setVerifyIncentiveRate] = useState(0);
  const [submittingVerify, setSubmittingVerify] = useState(false);

  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Alerts & Flagging State
  const [alerts, setAlerts] = useState(null);
  const [flaggedVisits, setFlaggedVisits] = useState([]);
  const [missedVisits, setMissedVisits] = useState(null);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [selectedVisitForFlag, setSelectedVisitForFlag] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [flagNotes, setFlagNotes] = useState("");
  const [flagging, setFlagging] = useState(false);

  // Auto-refresh State
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const autoRefreshRef = useRef(null);

  // Schedule View State (for combined schedule + visits view)
  const [scheduleView, setScheduleView] = useState([]);
  const [filterClient, setFilterClient] = useState("all");

  // Sandata EVV Export State
  const [sandataExportStartDate, setSandataExportStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [sandataExportEndDate, setSandataExportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [sandataIncludeClients, setSandataIncludeClients] = useState(false);
  const [sandataExporting, setSandataExporting] = useState(false);
  const [sandataValidating, setSandataValidating] = useState(false);
  const [sandataExportResult, setSandataExportResult] = useState(null);
  const [sandataReferenceData, setSandataReferenceData] = useState(null);
  const [sandataValidations, setSandataValidations] = useState({});
  const [validatingBatch, setValidatingBatch] = useState(false);
  const [complianceReport, setComplianceReport] = useState(null);
  const [checkingCompliance, setCheckingCompliance] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchData();
    getCurrentLocation();
    
    // Check for active visit
    checkActiveVisit();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAllVisits();
      fetchAlerts();
      fetchScheduleView();
    }
  }, [filterDate, filterEmployee, filterStatus, filterClient, isAdmin]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && isAdmin) {
      autoRefreshRef.current = setInterval(() => {
        fetchAllVisits();
        fetchScheduleView();
        setLastRefreshed(new Date());
      }, 60000); // Refresh every minute
    }
    
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefresh, isAdmin, filterDate, filterEmployee, filterStatus, filterClient]);

  const fetchScheduleView = async () => {
    try {
      // Get appointments for the date
      const appointmentsRes = await axios.get(
        `${API}/appointments?start_date=${filterDate}&end_date=${filterDate}`
      );
      const appointments = appointmentsRes.data;
      
      // Get visits for the date
      const visitsRes = await axios.get(`${API}/evv/visits?date=${filterDate}`);
      const visits = visitsRes.data;
      
      // Create visit lookup by appointment_id
      const visitsByAppointment = {};
      visits.forEach(v => {
        visitsByAppointment[v.appointment_id] = v;
      });
      
      // Merge appointments with visits
      const merged = appointments.map(appt => {
        const visit = visitsByAppointment[appt.id];
        const client = clients.find(c => c.id === appt.client_id);
        const employee = employees.find(e => e.id === appt.employee_id);
        
        return {
          appointment_id: appt.id,
          client_id: appt.client_id,
          client_name: client ? `${client.first_name} ${client.last_name}` : "Unknown",
          employee_id: appt.employee_id,
          employee_name: employee?.name || "Unassigned",
          schedule_start: `${appt.scheduled_date} ${appt.scheduled_time}`,
          schedule_end: appt.scheduled_end_time ? `${appt.scheduled_date} ${appt.scheduled_end_time}` : null,
          duration_minutes: appt.duration_minutes,
          // Visit data (if exists)
          visit_id: visit?.id || null,
          clock_in_time: visit?.clock_in_time || null,
          clock_out_time: visit?.clock_out_time || null,
          actual_duration: visit?.duration_minutes || null,
          visit_status: visit?.status || null,
          flagged: visit?.flagged || false,
          confirmed: visit?.status === "verified",
          missing_in: !visit && isAppointmentPast(appt),
          missing_out: visit && visit.status === "in_progress" && isVisitOverdue(visit, appt),
        };
      });
      
      // Apply filters
      let filtered = merged;
      if (filterClient !== "all") {
        filtered = filtered.filter(s => s.client_id === filterClient);
      }
      if (filterEmployee !== "all") {
        filtered = filtered.filter(s => s.employee_id === filterEmployee);
      }
      
      setScheduleView(filtered);
    } catch (error) {
      console.error("Failed to fetch schedule view:", error);
    }
  };

  const isAppointmentPast = (appt) => {
    const now = new Date();
    const scheduledTime = new Date(`${appt.scheduled_date}T${appt.scheduled_time}`);
    // Consider appointment past if more than 30 minutes after scheduled start
    return now > new Date(scheduledTime.getTime() + 30 * 60000);
  };

  const isVisitOverdue = (visit, appt) => {
    if (!visit?.clock_in_time) return false;
    const now = new Date();
    const clockIn = new Date(visit.clock_in_time);
    const expectedDuration = appt?.duration_minutes || 60;
    // Consider overdue if more than expected duration + 30 minutes
    return now > new Date(clockIn.getTime() + (expectedDuration + 30) * 60000);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientsRes, employeesRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/appointments?start_date=${new Date().toISOString().split('T')[0]}&end_date=${new Date().toISOString().split('T')[0]}`),
      ]);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
      setAppointments(appointmentsRes.data.filter(a => a.employee_id === user?.id || isAdmin));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveVisit = async () => {
    try {
      const response = await axios.get(`${API}/evv/active`);
      setActiveVisit(response.data);
    } catch (error) {
      // No active visit
      setActiveVisit(null);
    }
  };

  const fetchAllVisits = async () => {
    try {
      let url = `${API}/evv/visits?date=${filterDate}`;
      if (filterEmployee !== "all") url += `&employee_id=${filterEmployee}`;
      if (filterStatus !== "all") url += `&status=${filterStatus}`;
      
      const response = await axios.get(url);
      setVisits(response.data);
    } catch (error) {
      console.error("Failed to fetch visits:", error);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await axios.get(
        `${API}/evv/analytics?start_date=${reportStartDate}&end_date=${reportEndDate}`
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(
        `${API}/evv/export?start_date=${reportStartDate}&end_date=${reportEndDate}&format=csv`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evv_export_${reportStartDate}_to_${reportEndDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data");
    }
  };

  const fetchAlerts = async () => {
    try {
      const [alertsRes, flaggedRes, missedRes] = await Promise.all([
        axios.get(`${API}/evv/alerts`),
        axios.get(`${API}/evv/flagged`),
        axios.get(`${API}/evv/missed-visits`),
      ]);
      setAlerts(alertsRes.data);
      setFlaggedVisits(flaggedRes.data);
      setMissedVisits(missedRes.data);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    }
  };

  const handleFlagVisit = async () => {
    if (!flagReason.trim()) {
      toast.error("Please provide a reason for flagging");
      return;
    }

    setFlagging(true);
    try {
      await axios.patch(`${API}/evv/visits/${selectedVisitForFlag.id}/flag`, {
        reason: flagReason,
        notes: flagNotes,
      });
      toast.success("Visit flagged for review");
      setFlagDialogOpen(false);
      setSelectedVisitForFlag(null);
      setFlagReason("");
      setFlagNotes("");
      fetchAllVisits();
      fetchAlerts();
    } catch (error) {
      console.error("Failed to flag visit:", error);
      toast.error("Failed to flag visit");
    } finally {
      setFlagging(false);
    }
  };

  const handleUnflagVisit = async (visitId) => {
    try {
      await axios.patch(`${API}/evv/visits/${visitId}/unflag`);
      toast.success("Flag removed");
      fetchAllVisits();
      fetchAlerts();
    } catch (error) {
      console.error("Failed to unflag visit:", error);
      toast.error("Failed to remove flag");
    }
  };

  const openVerifyDialog = (visit) => {
    setVerifyingVisit(visit);
    setVerifyAwardIncentive(false);
    // Look up the caregiver's incentive rate so we can show it in the dialog
    const emp = employees.find((e) => e.id === visit.caregiver_id);
    setVerifyIncentiveRate(Number(emp?.documentation_incentive_rate || 0));
    setVerifyDialogOpen(true);
  };

  const handleSubmitVerify = async () => {
    if (!verifyingVisit) return;
    setSubmittingVerify(true);
    try {
      await axios.patch(`${API}/evv/visits/${verifyingVisit.id}/verify`, {
        award_incentive: verifyAwardIncentive,
      });
      toast.success(
        verifyAwardIncentive
          ? "Visit verified and incentive awarded"
          : "Visit verified"
      );
      setVerifyDialogOpen(false);
      setVerifyingVisit(null);
      fetchAllVisits();
      fetchAlerts();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to verify visit"
      );
    } finally {
      setSubmittingVerify(false);
    }
  };

  const handleVerifyVisit = async (visitId) => {
    // Legacy wrapper — now opens the dialog so admins can choose to award
    // the documentation incentive alongside verifying.
    const visit = visits.find((v) => v.id === visitId);
    if (visit) openVerifyDialog(visit);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
          setLocationError(null);
        },
        (error) => {
          setLocationError("Unable to get location. Please enable GPS.");
          console.error("Geolocation error:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setLocationError("Geolocation not supported by browser");
    }
  };

  const handleClockIn = async () => {
    if (!selectedAppointment) {
      toast.error("Please select an appointment");
      return;
    }
    
    if (!currentLocation) {
      toast.error("Location required for clock in. Please enable GPS.");
      getCurrentLocation();
      return;
    }

    setClockingIn(true);
    try {
      const response = await axios.post(`${API}/evv/clock-in`, {
        appointment_id: selectedAppointment,
        location: currentLocation,
        notes: clockInNotes,
      });
      
      setActiveVisit(response.data);
      toast.success("Clocked in successfully!");
      setClockInDialog(false);
      setSelectedAppointment("");
      setClockInNotes("");
      fetchAllVisits();
    } catch (error) {
      console.error("Failed to clock in:", error);
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    if (!currentLocation) {
      toast.error("Location required for clock out. Please enable GPS.");
      getCurrentLocation();
      return;
    }

    setClockingOut(true);
    try {
      await axios.post(`${API}/evv/clock-out`, {
        visit_id: activeVisit.id,
        location: currentLocation,
        notes: clockOutNotes,
        services_provided: servicesProvided,
      });
      
      setActiveVisit(null);
      toast.success("Clocked out successfully!");
      setClockOutDialog(false);
      setClockOutNotes("");
      setServicesProvided("");
      fetchAllVisits();
    } catch (error) {
      console.error("Failed to clock out:", error);
      toast.error(error.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockingOut(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : "Unknown";
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    const styles = {
      in_progress: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
      missed: "bg-red-100 text-red-700",
      verified: "bg-purple-100 text-purple-700",
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const todayAppointments = appointments.filter(a => 
    a.scheduled_date === new Date().toISOString().split('T')[0] &&
    (a.employee_id === user?.id || isAdmin)
  );

  // Sandata EVV Export Functions
  const fetchSandataReferenceData = async () => {
    try {
      const res = await axios.get(`${API}/evv/sandata/reference-data`);
      setSandataReferenceData(res.data);
    } catch (error) {
      console.error("Error fetching Sandata reference data:", error);
    }
  };

  const checkSandataCompliance = async () => {
    setCheckingCompliance(true);
    try {
      const res = await axios.get(`${API}/evv/sandata/compliance-check`);
      setComplianceReport(res.data);
      
      if (res.data.summary.overall_ready) {
        toast.success("All clients and employees are Sandata-compliant!");
      } else {
        const issues = res.data.summary.clients_with_issues + res.data.summary.employees_with_issues;
        toast.warning(`Found ${issues} records with compliance issues`);
      }
    } catch (error) {
      console.error("Error checking compliance:", error);
      toast.error("Failed to check Sandata compliance");
    } finally {
      setCheckingCompliance(false);
    }
  };

  const validateVisitsBatch = async (visitIds) => {
    if (!visitIds || visitIds.length === 0) return;
    
    setValidatingBatch(true);
    try {
      const res = await axios.post(`${API}/evv/sandata/validate-batch`, visitIds);
      setSandataValidations(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error("Error validating visits:", error);
    } finally {
      setValidatingBatch(false);
    }
  };

  // Auto-validate visits when visits list changes
  useEffect(() => {
    if (isAdmin && visits.length > 0) {
      const visitIds = visits.map(v => v.id);
      // Check which visits need validation
      const unvalidated = visitIds.filter(id => {
        // Use a check that doesn't depend on sandataValidations object reference
        return !Object.prototype.hasOwnProperty.call(sandataValidations, id);
      });
      if (unvalidated.length > 0) {
        validateVisitsBatch(unvalidated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, isAdmin]);

  const handleSandataExport = async () => {
    setSandataExporting(true);
    setSandataExportResult(null);
    
    try {
      const params = new URLSearchParams({
        start_date: sandataExportStartDate,
        end_date: sandataExportEndDate,
        include_clients: sandataIncludeClients.toString()
      });
      
      const res = await axios.get(`${API}/evv/sandata/export?${params}`);
      
      // Handle empty result (no completed visits)
      if (res.data.message && res.data.count === 0) {
        setSandataExportResult({
          metadata: {
            visit_count: 0,
            client_count: 0,
            format_version: "WI_DMS_EVV_v2.5",
            export_date: new Date().toISOString()
          },
          data: { Visit: [], ProviderIdentification: {} }
        });
        toast.info("No completed visits found for the specified date range");
        return;
      }
      
      setSandataExportResult(res.data);
      
      // Auto-download the JSON file only if there are visits
      if (res.data.data && res.data.metadata?.visit_count > 0) {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sandata_evv_export_${sandataExportStartDate}_to_${sandataExportEndDate}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${res.data.metadata?.visit_count || 0} visits to Sandata format`);
    } catch (error) {
      console.error("Sandata export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export Sandata EVV data");
    } finally {
      setSandataExporting(false);
    }
  };

  const handleValidateVisit = async (visitId) => {
    setSandataValidating(true);
    try {
      const res = await axios.get(`${API}/evv/sandata/validate/${visitId}`);
      const validation = res.data;
      
      if (validation.valid) {
        toast.success("Visit is Sandata-compliant and ready for export");
      } else {
        const errorMsg = validation.errors.join(", ");
        toast.error(`Validation failed: ${errorMsg}`);
      }
      
      return validation;
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Failed to validate visit");
      return null;
    } finally {
      setSandataValidating(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Fingerprint className="w-6 h-6" />
          Electronic Visit Verification (EVV)
        </h1>
        <p className="text-slate-500">Track and verify caregiver visits with GPS location</p>
      </div>

      {/* Location Status */}
      <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
        currentLocation ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
      }`}>
        <Navigation className={`w-5 h-5 ${currentLocation ? "text-emerald-600" : "text-amber-600"}`} />
        <div className="flex-1">
          <p className={`font-medium ${currentLocation ? "text-emerald-800" : "text-amber-800"}`}>
            {currentLocation ? "GPS Location Active" : "GPS Location Required"}
          </p>
          <p className={`text-sm ${currentLocation ? "text-emerald-600" : "text-amber-600"}`}>
            {currentLocation 
              ? `Accuracy: ${currentLocation.accuracy?.toFixed(0)}m` 
              : locationError || "Enable location services for EVV"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={getCurrentLocation} className="gap-1">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue={isAdmin ? "schedule" : "clock"} className="space-y-6">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="clock" className="gap-2" data-testid="tab-clock">
            <Clock className="w-4 h-4" />
            Clock In/Out
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="schedule" className="gap-2" data-testid="tab-schedule" onClick={fetchScheduleView}>
              <Calendar className="w-4 h-4" />
              Schedule
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="admin" className="gap-2" data-testid="tab-admin">
              <FileText className="w-4 h-4" />
              All Visits
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="alerts" className="gap-2" data-testid="tab-alerts" onClick={fetchAlerts}>
              <Bell className="w-4 h-4" />
              Alerts
              {alerts && (alerts.flagged_visits > 0 || alerts.missed_today > 0) && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {alerts.flagged_visits + alerts.missed_today}
                </span>
              )}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="reports" className="gap-2" data-testid="tab-reports" onClick={fetchAnalytics}>
              <BarChart3 className="w-4 h-4" />
              Reports
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="sandata" className="gap-2" data-testid="tab-sandata" onClick={fetchSandataReferenceData}>
              <FileJson className="w-4 h-4" />
              Sandata Export
            </TabsTrigger>
          )}
        </TabsList>

        {/* Schedule Tab */}
        {isAdmin && (
          <TabsContent value="schedule">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {/* Controls Bar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={autoRefresh}
                      onCheckedChange={setAutoRefresh}
                      id="auto-refresh"
                    />
                    <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
                      Auto Refresh (1 min)
                    </Label>
                  </div>
                  <span className="text-xs text-slate-500">
                    Last Refreshed: {lastRefreshed.toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1">
                    <Download className="w-4 h-4" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1">
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { fetchScheduleView(); setLastRefreshed(new Date()); }} className="gap-1">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Date:</Label>
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="w-40 h-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Client:</Label>
                  <Select value={filterClient} onValueChange={setFilterClient}>
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="All Clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Caregiver:</Label>
                  <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="All Caregivers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Caregivers</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Alert Summary */}
              {alerts && (alerts.missed_today > 0 || alerts.flagged_visits > 0) && (
                <div className="flex gap-4 mb-4">
                  {alerts.missed_today > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {alerts.missed_today} Missing Check-In
                    </div>
                  )}
                  {alerts.flagged_visits > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm">
                      <Flag className="w-4 h-4" />
                      {alerts.flagged_visits} Flagged
                    </div>
                  )}
                </div>
              )}

              {/* Schedule Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">Status</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Caregiver</TableHead>
                      <TableHead>Schedule Start</TableHead>
                      <TableHead>Schedule End</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead className="text-center">Confirmed</TableHead>
                      <TableHead className="text-center">Missing In</TableHead>
                      <TableHead className="text-center">Missing Out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleView.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No scheduled visits for this date
                        </TableCell>
                      </TableRow>
                    ) : (
                      scheduleView.map((item) => (
                        <TableRow 
                          key={item.appointment_id} 
                          className={`
                            ${item.flagged ? "bg-amber-50" : ""}
                            ${item.missing_in ? "bg-red-50" : ""}
                          `}
                        >
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {item.flagged && (
                                <div className="w-5 h-5 bg-amber-400 rounded flex items-center justify-center" title="Flagged">
                                  <Flag className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {item.missing_in && !item.flagged && (
                                <div className="w-5 h-5 bg-red-500 rounded flex items-center justify-center" title="Missing Check-In">
                                  <AlertTriangle className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {item.visit_status === "in_progress" && (
                                <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center" title="In Progress">
                                  <Zap className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {item.confirmed && (
                                <div className="w-5 h-5 bg-emerald-500 rounded flex items-center justify-center" title="Verified">
                                  <CheckCircle className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.client_name}</TableCell>
                          <TableCell>{item.employee_name}</TableCell>
                          <TableCell>
                            {item.schedule_start ? new Date(item.schedule_start).toLocaleString([], {
                              month: '2-digit', day: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : "-"}
                          </TableCell>
                          <TableCell>
                            {item.schedule_end ? new Date(item.schedule_end).toLocaleString([], {
                              month: '2-digit', day: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : `+${item.duration_minutes}m`}
                          </TableCell>
                          <TableCell>
                            {item.clock_in_time ? (
                              <span className="text-emerald-600">
                                {new Date(item.clock_in_time).toLocaleString([], {
                                  month: '2-digit', day: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.clock_out_time ? (
                              <span className="text-emerald-600">
                                {new Date(item.clock_out_time).toLocaleString([], {
                                  month: '2-digit', day: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.confirmed ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.missing_in ? (
                              <AlertTriangle className="w-5 h-5 text-red-500 mx-auto" />
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.missing_out ? (
                              <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto" />
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Clock In/Out Tab */}
        <TabsContent value="clock">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Visit Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Timer className="w-5 h-5 text-primary" />
                Current Status
              </h2>

              {activeVisit ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        IN PROGRESS
                      </span>
                      <span className="text-sm text-blue-600">
                        Started: {new Date(activeVisit.clock_in_time).toLocaleTimeString()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">
                      {getClientName(activeVisit.client_id)}
                    </h3>
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {activeVisit.clock_in_address || "Location recorded"}
                    </p>
                  </div>

                  <Button 
                    className="w-full gap-2 bg-red-600 hover:bg-red-700"
                    onClick={() => {
                      getCurrentLocation();
                      setClockOutDialog(true);
                    }}
                  >
                    <Square className="w-4 h-4" />
                    Clock Out
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 mb-4">No active visit</p>
                  <Button 
                    className="gap-2"
                    onClick={() => {
                      getCurrentLocation();
                      setClockInDialog(true);
                    }}
                    disabled={!currentLocation}
                  >
                    <Play className="w-4 h-4" />
                    Clock In
                  </Button>
                </div>
              )}
            </div>

            {/* Today's Schedule */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Today&apos;s Schedule
              </h2>

              {todayAppointments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No appointments scheduled for today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayAppointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="p-3 border border-slate-200 rounded-lg hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800">
                          {appt.client_name || getClientName(appt.client_id)}
                        </span>
                        <span className="text-sm text-slate-500">
                          {appt.scheduled_time}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {appt.appointment_type} • {appt.duration_minutes} min
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Admin Tab */}
        {isAdmin && (
          <TabsContent value="admin">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Visit Records</h2>
                  <p className="text-sm text-slate-500">View and manage all EVV records</p>
                </div>
                <Button variant="outline" className="gap-2" onClick={fetchAllVisits}>
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Employee</Label>
                  <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="missed">Missed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Visits Table */}
              {visits.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No visit records found for selected filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Clock In</TableHead>
                        <TableHead>Clock Out</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <FileJson className="w-3 h-3" />
                            Sandata
                          </div>
                        </TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visits.map((visit) => (
                        <TableRow key={visit.id} className={visit.flagged ? "bg-red-50" : ""}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {visit.flagged && <Flag className="w-3 h-3 text-red-500" />}
                              {getEmployeeName(visit.employee_id)}
                            </div>
                          </TableCell>
                          <TableCell>{getClientName(visit.client_id)}</TableCell>
                          <TableCell>
                            {visit.clock_in_time 
                              ? new Date(visit.clock_in_time).toLocaleTimeString() 
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {visit.clock_out_time 
                              ? new Date(visit.clock_out_time).toLocaleTimeString() 
                              : "-"}
                          </TableCell>
                          <TableCell>{formatDuration(visit.duration_minutes)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(visit.status)}`}>
                              {visit.status?.replace("_", " ").toUpperCase()}
                            </span>
                          </TableCell>
                          <TableCell>
                            {visit.clock_in_location ? (
                              <a
                                href={`https://maps.google.com/?q=${visit.clock_in_location.latitude},${visit.clock_in_location.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1"
                              >
                                <MapPin className="w-3 h-3" />
                                View
                              </a>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const validation = sandataValidations[visit.id];
                              if (!validation) {
                                return (
                                  <span className="text-slate-400 text-xs">
                                    {validatingBatch ? "..." : "-"}
                                  </span>
                                );
                              }
                              
                              if (validation.sandata_ready) {
                                return (
                                  <div className="flex items-center gap-1 justify-center" title="Ready for Sandata export">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span className="text-xs text-emerald-600 font-medium">Ready</span>
                                  </div>
                                );
                              }
                              
                              const issues = [...(validation.errors || []), ...(validation.warnings || [])];
                              return (
                                <div 
                                  className="flex items-center gap-1 justify-center cursor-help"
                                  title={issues.join(", ")}
                                >
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                  <span className="text-xs text-amber-600">{validation.errors?.length || 0} issues</span>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 justify-center">
                              {visit.status === "completed" && !visit.flagged && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-emerald-600"
                                  onClick={() => handleVerifyVisit(visit.id)}
                                  title="Verify Visit"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {!visit.flagged ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-amber-600"
                                  onClick={() => {
                                    setSelectedVisitForFlag(visit);
                                    setFlagDialogOpen(true);
                                  }}
                                  title="Flag for Review"
                                >
                                  <Flag className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-slate-600"
                                  onClick={() => handleUnflagVisit(visit.id)}
                                  title="Remove Flag"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* Alerts Tab */}
        {isAdmin && (
          <TabsContent value="alerts">
            <div className="space-y-6">
              {/* Alert Summary Cards */}
              {alerts && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Flagged Visits</p>
                        <p className="text-2xl font-bold text-red-600">{alerts.flagged_visits}</p>
                      </div>
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Flag className="w-5 h-5 text-red-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Missed Today</p>
                        <p className="text-2xl font-bold text-amber-600">{alerts.missed_today}</p>
                      </div>
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Pending Verify</p>
                        <p className="text-2xl font-bold text-blue-600">{alerts.pending_verification}</p>
                      </div>
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Active Now</p>
                        <p className="text-2xl font-bold text-emerald-600">{alerts.active_visits}</p>
                      </div>
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Play className="w-5 h-5 text-emerald-600" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Flagged Visits Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-500" />
                  Flagged Visits ({flaggedVisits.length})
                </h3>
                {flaggedVisits.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No flagged visits</p>
                ) : (
                  <div className="space-y-3">
                    {flaggedVisits.map((visit) => (
                      <div key={visit.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-800">
                              {visit.employee_name} → {visit.client_name}
                            </p>
                            <p className="text-sm text-slate-600">
                              {visit.clock_in_time?.split('T')[0]} at{' '}
                              {visit.clock_in_time && new Date(visit.clock_in_time).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-red-600 mt-1">
                              <strong>Reason:</strong> {visit.flag_reason}
                            </p>
                            {visit.flag_notes && (
                              <p className="text-sm text-slate-500 mt-1">{visit.flag_notes}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Flagged by {visit.flagged_by} on {visit.flagged_at?.split('T')[0]}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnflagVisit(visit.id)}
                            className="text-slate-600"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Clear
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Missed Visits Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Missed Visits Today ({missedVisits?.missed_count || 0} of {missedVisits?.total_appointments || 0})
                </h3>
                {!missedVisits || missedVisits.missed_count === 0 ? (
                  <p className="text-center py-8 text-slate-500">
                    All scheduled visits have been clocked in
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Scheduled Time</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {missedVisits.missed_visits.map((appt) => (
                          <TableRow key={appt.appointment_id} className="bg-amber-50">
                            <TableCell className="font-medium">{appt.employee_name}</TableCell>
                            <TableCell>{appt.client_name}</TableCell>
                            <TableCell>{appt.scheduled_time}</TableCell>
                            <TableCell className="capitalize">{appt.appointment_type}</TableCell>
                            <TableCell>{appt.duration_minutes} min</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* Reports Tab */}
        {isAdmin && (
          <TabsContent value="reports">
            <div className="space-y-6">
              {/* Date Range Filter */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="date"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Date</Label>
                    <Input
                      type="date"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <Button onClick={fetchAnalytics} className="gap-2" disabled={analyticsLoading}>
                    <RefreshCw className={`w-4 h-4 ${analyticsLoading ? 'animate-spin' : ''}`} />
                    Generate Report
                  </Button>
                  <Button variant="outline" onClick={handleExportCSV} className="gap-2">
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <div className="spinner w-8 h-8 mx-auto"></div>
                  <p className="text-slate-500 mt-2">Loading analytics...</p>
                </div>
              ) : analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Visits</p>
                          <p className="text-2xl font-bold text-slate-900">{analytics.summary.total_visits}</p>
                        </div>
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Hours</p>
                          <p className="text-2xl font-bold text-slate-900">{analytics.summary.total_hours}h</p>
                        </div>
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-emerald-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Avg Duration</p>
                          <p className="text-2xl font-bold text-slate-900">{analytics.summary.avg_duration_minutes}m</p>
                        </div>
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Timer className="w-5 h-5 text-amber-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Compliance Rate</p>
                          <p className="text-2xl font-bold text-slate-900">{analytics.summary.compliance_rate}%</p>
                        </div>
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Award className="w-5 h-5 text-purple-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Visit Status Breakdown */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Visit Status Breakdown
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                            Completed
                          </span>
                          <span className="font-semibold">{analytics.summary.completed}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                            Verified
                          </span>
                          <span className="font-semibold">{analytics.summary.verified}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            In Progress
                          </span>
                          <span className="font-semibold">{analytics.summary.in_progress}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-red-500"></span>
                            Missed
                          </span>
                          <span className="font-semibold">{analytics.summary.missed}</span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="h-4 rounded-full bg-slate-100 overflow-hidden flex">
                          {analytics.summary.total_visits > 0 && (
                            <>
                              <div 
                                className="bg-purple-500 h-full" 
                                style={{ width: `${(analytics.summary.verified / analytics.summary.total_visits) * 100}%` }}
                              ></div>
                              <div 
                                className="bg-emerald-500 h-full" 
                                style={{ width: `${((analytics.summary.completed - analytics.summary.verified) / analytics.summary.total_visits) * 100}%` }}
                              ></div>
                              <div 
                                className="bg-blue-500 h-full" 
                                style={{ width: `${(analytics.summary.in_progress / analytics.summary.total_visits) * 100}%` }}
                              ></div>
                              <div 
                                className="bg-red-500 h-full" 
                                style={{ width: `${(analytics.summary.missed / analytics.summary.total_visits) * 100}%` }}
                              ></div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Daily Breakdown */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        Daily Activity
                      </h3>
                      {analytics.daily_breakdown.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">No data for selected period</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {analytics.daily_breakdown.map((day) => (
                            <div key={day.date} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                              <span className="text-sm text-slate-600">{day.date}</span>
                              <div className="flex items-center gap-4">
                                <span className="text-sm">
                                  <span className="font-medium">{day.visits}</span> visits
                                </span>
                                <span className="text-sm text-slate-500">
                                  {day.hours.toFixed(1)}h
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Employee Performance */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Employee Performance
                    </h3>
                    {analytics.by_employee.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No employee data for selected period</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee</TableHead>
                              <TableHead className="text-center">Total Visits</TableHead>
                              <TableHead className="text-center">Completed</TableHead>
                              <TableHead className="text-center">Verified</TableHead>
                              <TableHead className="text-center">Total Hours</TableHead>
                              <TableHead className="text-center">Verification Rate</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {analytics.by_employee.map((emp) => (
                              <TableRow key={emp.employee_id}>
                                <TableCell className="font-medium">{emp.employee_name}</TableCell>
                                <TableCell className="text-center">{emp.total_visits}</TableCell>
                                <TableCell className="text-center">{emp.completed}</TableCell>
                                <TableCell className="text-center">{emp.verified}</TableCell>
                                <TableCell className="text-center">{emp.total_hours}h</TableCell>
                                <TableCell className="text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    emp.verification_rate >= 90 
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : emp.verification_rate >= 70
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {emp.verification_rate}%
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Select date range and click &quot;Generate Report&quot;</p>
                </div>
              )}
            </div>
          </TabsContent>
        )}


        {/* Sandata EVV Export Tab - Using extracted component */}
        {isAdmin && (
          <TabsContent value="sandata">
            <SandataExportTab
              sandataExportStartDate={sandataExportStartDate}
              setSandataExportStartDate={setSandataExportStartDate}
              sandataExportEndDate={sandataExportEndDate}
              setSandataExportEndDate={setSandataExportEndDate}
              sandataIncludeClients={sandataIncludeClients}
              setSandataIncludeClients={setSandataIncludeClients}
              sandataExporting={sandataExporting}
              sandataExportResult={sandataExportResult}
              handleSandataExport={handleSandataExport}
              checkingCompliance={checkingCompliance}
              complianceReport={complianceReport}
              checkSandataCompliance={checkSandataCompliance}
              sandataReferenceData={sandataReferenceData}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Clock In Dialog */}
      <Dialog open={clockInDialog} onOpenChange={setClockInDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              Clock In
            </DialogTitle>
            <DialogDescription>
              Start your visit by selecting the appointment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Appointment *</Label>
              <Select value={selectedAppointment} onValueChange={setSelectedAppointment}>
                <SelectTrigger data-testid="clock-in-appointment">
                  <SelectValue placeholder="Choose appointment..." />
                </SelectTrigger>
                <SelectContent>
                  {todayAppointments.map((appt) => (
                    <SelectItem key={appt.id} value={appt.id}>
                      {appt.client_name || getClientName(appt.client_id)} - {appt.scheduled_time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-1">Current Location</p>
              {currentLocation ? (
                <p className="text-xs text-slate-500">
                  {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                  <span className="ml-2">(±{currentLocation.accuracy?.toFixed(0)}m)</span>
                </p>
              ) : (
                <p className="text-xs text-red-500">Location not available</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={clockInNotes}
                onChange={(e) => setClockInNotes(e.target.value)}
                placeholder="Any notes about the visit start..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockInDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleClockIn} disabled={clockingIn || !currentLocation}>
              {clockingIn ? "Clocking In..." : "Clock In"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clock Out Dialog */}
      <Dialog open={clockOutDialog} onOpenChange={setClockOutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="w-5 h-5 text-red-600" />
              Clock Out
            </DialogTitle>
            <DialogDescription>
              Complete your visit and record services provided
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {activeVisit && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800">
                  {getClientName(activeVisit.client_id)}
                </p>
                <p className="text-xs text-blue-600">
                  Started: {new Date(activeVisit.clock_in_time).toLocaleTimeString()}
                </p>
              </div>
            )}

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-1">Current Location</p>
              {currentLocation ? (
                <p className="text-xs text-slate-500">
                  {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                </p>
              ) : (
                <p className="text-xs text-red-500">Location not available</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Services Provided *</Label>
              <Textarea
                value={servicesProvided}
                onChange={(e) => setServicesProvided(e.target.value)}
                placeholder="List services provided during the visit..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockOutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClockOut} 
              disabled={clockingOut || !currentLocation || !servicesProvided.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {clockingOut ? "Clocking Out..." : "Clock Out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Visit Dialog */}
      <Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-500" />
              Flag Visit for Review
            </DialogTitle>
            <DialogDescription>
              Flag this visit for administrative review
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedVisitForFlag && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-800">
                  {selectedVisitForFlag.employee_name || getEmployeeName(selectedVisitForFlag.employee_id)}
                </p>
                <p className="text-xs text-slate-500">
                  Client: {selectedVisitForFlag.client_name || getClientName(selectedVisitForFlag.client_id)}
                </p>
                <p className="text-xs text-slate-500">
                  {selectedVisitForFlag.clock_in_time?.split('T')[0]} at{' '}
                  {selectedVisitForFlag.clock_in_time && new Date(selectedVisitForFlag.clock_in_time).toLocaleTimeString()}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason for Flag *</Label>
              <Select value={flagReason} onValueChange={setFlagReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Location discrepancy">Location discrepancy</SelectItem>
                  <SelectItem value="Time discrepancy">Time discrepancy</SelectItem>
                  <SelectItem value="Duration too short">Duration too short</SelectItem>
                  <SelectItem value="Duration too long">Duration too long</SelectItem>
                  <SelectItem value="Missing clock out">Missing clock out</SelectItem>
                  <SelectItem value="Client complaint">Client complaint</SelectItem>
                  <SelectItem value="Services incomplete">Services incomplete</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={flagNotes}
                onChange={(e) => setFlagNotes(e.target.value)}
                placeholder="Provide additional details..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setFlagDialogOpen(false);
              setSelectedVisitForFlag(null);
              setFlagReason("");
              setFlagNotes("");
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleFlagVisit} 
              disabled={flagging || !flagReason}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {flagging ? "Flagging..." : "Flag Visit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Visit dialog with Documentation Incentive checkbox */}
      <Dialog open={verifyDialogOpen} onOpenChange={(o) => { if (!o) { setVerifyDialogOpen(false); setVerifyingVisit(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="verify-visit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Verify Visit
            </DialogTitle>
            <DialogDescription>
              {verifyingVisit ? (
                <>
                  Verifying visit for{" "}
                  <span className="font-medium">
                    {verifyingVisit.client_name || "client"}
                  </span>{" "}
                  by{" "}
                  <span className="font-medium">
                    {verifyingVisit.caregiver_name || "caregiver"}
                  </span>.
                </>
              ) : "Verify this visit."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {verifyIncentiveRate > 0 ? (
              <label
                className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors border-slate-200 hover:bg-slate-50 has-[:checked]:border-emerald-300 has-[:checked]:bg-emerald-50"
                data-testid="verify-award-incentive-toggle"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-emerald-600"
                  checked={verifyAwardIncentive}
                  onChange={(e) => setVerifyAwardIncentive(e.target.checked)}
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-900">
                    Documentation is accurate & complete on time — award incentive
                  </span>
                  <span className="block text-xs text-slate-600 mt-0.5">
                    Adds ${verifyIncentiveRate.toFixed(2)}/hour to this
                    caregiver's shift pay for this visit.
                  </span>
                </span>
              </label>
            ) : (
              <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
                This caregiver has no documentation incentive rate on their profile — set one to enable the incentive award.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setVerifyDialogOpen(false); setVerifyingVisit(null); }}
              disabled={submittingVerify}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitVerify}
              disabled={submittingVerify}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="verify-visit-confirm-btn"
            >
              {submittingVerify ? "Verifying…" : "Verify Visit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
